
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllIndustries } from "../data/industriesData";
import ProductGrid from "../components/Sections/ProductGrid";

const IndustriesServed = () => {
  const industriesRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const industries = getAllIndustries();
  const [isPaused, setIsPaused] = useState(false);

  useGSAP(() => {
    gsap.fromTo(
      industriesRef.current.querySelectorAll('.industry-card'),
      { y: 20, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        ease: "power2.out",
        stagger: 0.05,
        scrollTrigger: {
          trigger: industriesRef.current,
          start: "top 85%",
        }
      }
    );
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    let animationFrameId;
    const smoothScroll = () => {
      if (!isPaused && container) {
        container.scrollLeft += 0.8; 
        const maxScroll = container.scrollWidth / 2;
        if (container.scrollLeft >= maxScroll) {
          container.scrollLeft = 0;
        }
      }
      animationFrameId = requestAnimationFrame(smoothScroll);
    };
    animationFrameId = requestAnimationFrame(smoothScroll);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isPaused]);

  const duplicatedIndustries = [...industries, ...industries];

  return (
    <section ref={industriesRef} id="industries" className="pt-32 pb-4 px-6 bg-white  relative overflow-hidden">
      {/* Background Gradient Accents */}
      <div className="absolute top-12 left-1/4 w-72 h-72 bg-[#952877]/5 blur-[100px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto text-center relative z-10">
        <h2 className="text-4xl md:text-7xl font-extrabold text-[#17091a] mb-6">
          Industries We <span className="text-[#952877]">Transform</span>
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-16 leading-relaxed">
          We deliver specialized AI solutions that drive innovation across global sectors.
        </p>
        
        <div className="max-w-full mb-12 relative group/container">
          {/* Edge Fading Gradients (Critical for the "Infinite" look) */}
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent z-20 pointer-events-none"></div>
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent z-20 pointer-events-none"></div>

          <div 
            ref={scrollContainerRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="overflow-x-auto overflow-y-hidden"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <style>{`div::-webkit-scrollbar { display: none; }`}</style>
            
            <div className="flex gap-8 py-8 px-4">
              {duplicatedIndustries.map((industry, index) => (
                <div 
                  key={`${industry.id}-${index}`}
                  className="industry-card group relative p-6 rounded-2xl bg-white border border-gray-100 hover:border-[#952877]/30 transition-all duration-500 min-w-[180px] w-[180px] shadow-sm hover:shadow-[0_15px_35px_rgba(89,42,167,0.1)] cursor-pointer flex-shrink-0"
                >
                  {/* Subtle hover background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#952877]/5 to-[#592aa7]/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
                  
                  <div className="relative z-10">
                    <div className="mb-4 bg-gray-50 group-hover:bg-white w-16 h-16 rounded-xl flex items-center justify-center mx-auto transition-colors duration-300 shadow-inner group-hover:shadow-md">
                      <img 
                        src={industry.image} 
                        className="w-10 h-10 grayscale group-hover:grayscale-0 border border-pink-500 transition-all duration-500" 
                        alt={industry.name}/>
                    </div>
                    <div className="font-bold text-sm text-gray-700 group-hover:text-[#17091a] text-center leading-tight">
                      {industry.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <Link 
          to="/industries" 
          className="inline-flex items-center text-[#592aa7] hover:text-[#952877] transition-all text-lg font-bold group"
        >
          Explore All Industries 
          <div className="ml-3 p-1 rounded-full bg-gray-50 group-hover:bg-[#952877]/10 transition-colors">
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
            </svg>
          </div>
        </Link>
      </div>
      <ProductGrid/>
    </section>
  );
};

export default IndustriesServed;

